import React, { Component } from "react";
import config from 'react-global-configuration';
import jwt_decode from "jwt-decode";
import axios from "axios";
import ColumnChart from "../amcharts/ColumnChart";
import PieChart from "../amcharts/PieChart";
import LineChart from "../amcharts/LineChart";
import StackedColumnWithLineChart from "../amcharts/StackedColumnWithLineChart";
import IndiaMapChart from "../amcharts/IndiaMapChart";
import WaterfallChart from "../amcharts/WaterfallChart";
import CohortGridChart from "../amcharts/CohortGridChart";

/**
 * Route this at: /admin/sparcs-ai-chat  (inside AdminLayout Switch, Admin.jsx)
 * Opens in a new tab from AskSparcsAI.jsx -> window.open('/admin/sparcs-ai-chat')
 * Same-origin, so localStorage.usertoken is already available here.
 */

const ROLE_META = {
    Innoviti: {
        label: 'Internal User',
        color: '#5e5ce6',
        bg: '#EFEEFE',
        icon: 'fa-shield',
        desc: 'Platform-wide access'
    },
    Merchant: {
        label: 'Merchant Access',
        color: '#0f9d58',
        bg: '#E9F9EF',
        icon: 'fa-building',
        desc: 'Scoped to merchant(s) below'
    },
    Chain: {
        label: 'Chain Access',
        color: '#e67e22',
        bg: '#FDF1E6',
        icon: 'fa-sitemap',
        desc: 'Scoped to chain(s) below'
    },
    Store: {
        label: 'Store Access',
        color: '#e0507a',
        bg: '#FCEAF0',
        icon: 'fa-shopping-bag',
        desc: 'Scoped to store(s) below'
    }
};

class AskSparcsAIChat extends Component {
    constructor(props) {
        super(props);
        this.state = {
            messages: [],
            input: '',
            loading: false,
            loadingContext: true,
            userName: '',
            object_type: '',
            access_list: [],
            expandedChart: null
        };
        this.messagesEndRef = React.createRef();
    }

    componentDidMount() {
        const token = localStorage.usertoken;
        if (!token) {
            window.location.href = "/";
            return;
        }
        try {
            const decoded = jwt_decode(token);
            this.setState({
                userName: (decoded.firstName || '') + ' ' + (decoded.lastName || ''),
                object_type: decoded.object_type
            });
        } catch (err) {
            console.log("token decode failed");
        }

        this.loadAccessContext();
    }

    loadAccessContext = () => {
        const token = localStorage.usertoken;
        axios.post(config.get('API_URL') + '/sparcs-ai/access-context', {}, {
            headers: { Authorization: ` ${token}` }
        }).then(response => {
            const data = response.data;
            this.setState({
                object_type: data.object_type || this.state.object_type,
                access_list: data.access_list || [],
                loadingContext: false,
                messages: [{
                    role: 'assistant',
                    text: 'Hi! Ask me anything about your transactions or summary data — I\'ll keep the answers scoped to the access shown above.'
                }]
            });
        }).catch(err => {
            console.log(err);
            this.setState({
                loadingContext: false,
                messages: [{ role: 'assistant', text: 'Hi! Ask me anything about your transactions or summary data.' }]
            });
        });
    }

    scrollToBottom = () => {
        if (this.messagesEndRef.current) {
            this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }

    componentDidUpdate() {
        this.scrollToBottom();
    }

    onChangeInput = (e) => {
        this.setState({ input: e.target.value });
    }

    onKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.sendMessage();
        }
    }

    sendMessage = () => {
        const question = this.state.input.trim();
        if (!question || this.state.loading) return;

        const token = localStorage.usertoken;

        this.setState({
            messages: [...this.state.messages, { role: 'user', text: question }],
            input: '',
            loading: true
        });

        // object_type/object_code are read on the backend from the Authorization
        // token itself - never trusted from the frontend.
        axios.post(config.get('API_URL') + '/sparcs-ai/chat', {
            question: question
        }, {
            headers: { Authorization: ` ${token}` }
        }).then(response => {
            // Expected backend response shape:
            // {
            //   answer_text: "...",
            //   table: { columns: [...], rows: [...] } | null,
            //   chart: { chart_type: "Column"|"Pie", data: [...], x_axis: "col", y_axis: ["col"], name: "..." } | null
            // }
            const data = response.data;
            this.setState({
                messages: [...this.state.messages, {
                    role: 'assistant',
                    text: data.answer_text,
                    table: data.table || null,
                    chart: data.chart || null
                }],
                loading: false
            });
        }).catch(err => {
            console.log(err);
            this.setState({
                messages: [...this.state.messages, {
                    role: 'assistant',
                    text: "Sorry, something went wrong. Please try again."
                }],
                loading: false
            });
        });
    }

    renderTable(table) {
        if (!table || !table.rows || table.rows.length === 0) return null;
        return (
            <div className="table-responsive" style={{ marginTop: '10px' }}>
                <table className="table table-striped table-bordered" style={{ fontSize: '13px' }}>
                    <thead>
                        <tr>
                            {table.columns.map((col, i) => <th key={i}>{col}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {table.rows.map((row, i) => (
                            <tr key={i}>
                                {table.columns.map((col, j) => <td key={j}>{row[col]}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderChart(chart, expanded) {
        if (!chart) return null;
        // Reuses the existing Sparcs amcharts wrapper components (same ones DashboardDetails.jsx
        // uses) so chat charts look consistent with the rest of the portal - chart_type/data/
        // x_axis/y_axis are built server-side (SparcsAIHelper.buildChartFromRows), never by the LLM.
        const isPie = chart.chart_type === 'Pie';
        const isStackedLine = chart.chart_type === 'StackedColumnLine';
        const isIndiaMap = chart.chart_type === 'IndiaMap';
        const isWaterfall = chart.chart_type === 'Waterfall';
        const isCohort = chart.chart_type === 'Cohort';
        const ChartComponent = isPie ? PieChart
            : isStackedLine ? StackedColumnWithLineChart
            : isIndiaMap ? IndiaMapChart
            : isWaterfall ? WaterfallChart
            : isCohort ? CohortGridChart
            : (chart.chart_type === 'Line' ? LineChart : ColumnChart);
        return (
            <div style={{ width: '100%', marginTop: '10px', position: 'relative' }}>
                {!expanded &&
                    <span
                        onClick={() => this.setState({ expandedChart: chart })}
                        title="Expand chart"
                        style={{
                            position: 'absolute', top: '4px', right: '4px', zIndex: 5,
                            cursor: 'pointer', color: '#999', background: '#fff',
                            borderRadius: '4px', padding: '2px 6px', fontSize: '13px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                        }}
                    >
                        <i className="fa fa-expand"></i>
                    </span>
                }
                <ChartComponent
                    data={chart.data}
                    x_axis={chart.x_axis}
                    // PieChart's valueField wants a single field name string;
                    // ColumnChart/StackedColumnWithLineChart's graphs map over an array of value fields.
                    y_axis={isPie ? chart.y_axis[0] : chart.y_axis}
                    chartHeight={expanded ? '550px' : '250px'}
                    name={chart.name}
                    // Multi-series line chart (e.g. one line per tender mode) - force all lines
                    // onto a single shared left-hand axis instead of amcharts3's default of one
                    // axis per line (which overlaps/clutters the right side with 5-6 series).
                    // No-op for PieChart/ColumnChart, which ignore unknown props.
                    singleAxis={chart.chart_type === 'Line' && chart.y_axis.length > 1}
                    // Only used by StackedColumnWithLineChart (the secondary-axis % line) -
                    // harmless no-op prop for every other chart type.
                    line_field={chart.line_field}
                    line_title={chart.line_title}
                    // Only used by StackedColumnWithLineChart (the non-stacked count/amount
                    // shown in a bar's hover balloon, e.g. Link's funnel chart) - harmless no-op
                    // elsewhere.
                    tooltip_field_map={chart.tooltip_field_map}
                    // Only used by IndiaMapChart (city-wise bubble markers on top of the state
                    // heat map, plus the explicit "Total: X" line and its unit label) - harmless
                    // no-op props for every other chart type.
                    city_data={chart.city_data}
                    total={chart.total}
                    total_label={chart.total_label}
                    // Only used by CohortGridChart (the acquisition-cohort retention grid) - it
                    // ignores data/x_axis/y_axis entirely and reads columns/rows instead; harmless
                    // no-op props for every other chart type.
                    columns={chart.columns}
                    rows={chart.rows}
                />
            </div>
        );
    }

    renderAccessCard() {
        const { object_type, access_list, userName, loadingContext } = this.state;
        const meta = ROLE_META[object_type] || ROLE_META['Innoviti'];
        const initials = userName.trim()
            ? userName.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
            : 'U';

        if (loadingContext) {
            return <div style={{ fontSize: '12px', color: '#999' }}>Checking your access...</div>;
        }

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: '#fff',
                borderRadius: '10px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                padding: '8px 14px',
                maxWidth: '420px'
            }}>
                <div style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '50%',
                    backgroundColor: '#5e5ce6',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    flexShrink: 0
                }}>
                    {initials}
                </div>

                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#222', lineHeight: '1.3' }}>
                        {userName || 'Logged in user'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '1px 9px',
                            borderRadius: '20px',
                            backgroundColor: meta.bg,
                            color: meta.color,
                            fontSize: '11px',
                            fontWeight: '600',
                            marginRight: '8px'
                        }}>
                            <i className={`fa ${meta.icon}`} style={{ marginRight: '5px' }}></i>
                            {meta.label}
                        </span>
                        <span style={{ fontSize: '11px', color: '#999' }}>
                            {meta.desc}
                        </span>
                    </div>

                    {access_list.length > 0 &&
                        <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {access_list.slice(0, 4).map((item, i) => (
                                <span key={i} style={{
                                    padding: '2px 8px',
                                    borderRadius: '5px',
                                    backgroundColor: '#f4f4f8',
                                    border: '1px solid #e6e6ee',
                                    fontSize: '11px',
                                    color: '#333'
                                }}>
                                    {item.name || item.code}
                                </span>
                            ))}
                            {access_list.length > 4 &&
                                <span style={{ fontSize: '11px', color: '#999', alignSelf: 'center' }}>
                                    +{access_list.length - 4} more
                                </span>
                            }
                        </div>
                    }
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className="content" style={{ padding: '10px 24px', width: '100%', boxSizing: 'border-box' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginBottom: '10px'
                }}>
                    <div>
                        <h5 style={{ fontWeight: '700', color: '#333', margin: 0 }}>
                            <i className="fa fa-comments" style={{ color: '#5e5ce6', marginRight: '8px' }}></i>
                            Ask Innoviti Sparcs AI
                        </h5>
                        <p style={{ color: '#888', margin: '2px 0 0', fontSize: '12px' }}>
                            Chat with your transaction and summary data — scoped to your access level.
                        </p>
                    </div>

                    {this.renderAccessCard()}
                </div>

                <div style={{
                    background: '#fff',
                    borderRadius: '10px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    padding: '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    <div
                        style={{
                            height: 'calc(100vh - 260px)',
                            minHeight: '400px',
                            overflowY: 'auto',
                            padding: '10px',
                            borderRadius: '6px',
                            backgroundColor: '#fafafa'
                        }}
                    >
                        {this.state.messages.map((msg, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                    marginBottom: '14px'
                                }}
                            >
                                <div
                                    style={{
                                        // Messages carrying a chart/table need real width to be readable -
                                        // only plain-text replies stay capped at 70%.
                                        maxWidth: (msg.table || msg.chart) ? '95%' : '70%',
                                        width: (msg.table || msg.chart) ? '95%' : 'auto',
                                        padding: '10px 14px',
                                        borderRadius: '14px',
                                        borderBottomRightRadius: msg.role === 'user' ? '4px' : '14px',
                                        borderBottomLeftRadius: msg.role === 'user' ? '14px' : '4px',
                                        backgroundColor: msg.role === 'user' ? '#5e5ce6' : '#fff',
                                        color: msg.role === 'user' ? '#fff' : '#333',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        whiteSpace: 'pre-wrap',
                                        fontSize: '14px'
                                    }}
                                >
                                    {msg.text}
                                    {msg.table && this.renderTable(msg.table)}
                                    {msg.chart && this.renderChart(msg.chart)}
                                </div>
                            </div>
                        ))}
                        {this.state.loading &&
                            <div style={{ color: '#999', fontStyle: 'italic', fontSize: '13px', paddingLeft: '4px' }}>
                                <i className="fa fa-circle-o-notch fa-spin" style={{ marginRight: '6px' }}></i>
                                Innoviti Sparcs AI is typing...
                            </div>
                        }
                        <div ref={this.messagesEndRef} />
                    </div>

                    <div style={{ display: 'flex', marginTop: '12px' }}>
                        <textarea
                            className="form-control"
                            rows="2"
                            placeholder="Ask about transactions or summary data..."
                            value={this.state.input}
                            onChange={this.onChangeInput}
                            onKeyPress={this.onKeyPress}
                            style={{ resize: 'none', borderRadius: '8px' }}
                        />
                        <button
                            type="button"
                            className="btn btn-primary"
                            style={{ marginLeft: '10px', borderRadius: '8px', padding: '0 22px' }}
                            onClick={this.sendMessage}
                            disabled={this.state.loading}
                        >
                            <i className="fa fa-paper-plane" style={{ marginRight: '6px' }}></i>
                            Send
                        </button>
                    </div>
                </div>

                {this.state.expandedChart &&
                    <div
                        onClick={() => this.setState({ expandedChart: null })}
                        style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(0,0,0,0.6)', zIndex: 99999,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: '#fff', borderRadius: '10px', padding: '20px',
                                width: '90vw', maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <span
                                    onClick={() => this.setState({ expandedChart: null })}
                                    style={{ cursor: 'pointer', fontSize: '20px', color: '#999' }}
                                >&times;</span>
                            </div>
                            {this.renderChart(this.state.expandedChart, true)}
                        </div>
                    </div>
                }
            </div>
        );
    }
}

export default AskSparcsAIChat;